import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import auth from '../utils/auth';
import { updateUserProfile, getUserProfile } from '../utils/api_dashboard';
import Sidebar from '../components/dash/Sidebar';
import Navbar from '../components/dash/Navbar';
import FloatingNavToggle from '../components/dash/FloatingNavToggle';

const MyProfile = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: ''
  });
  const [user, setUser] = useState(auth.getCurrentUser() || {});
  const [pageLoading, setPageLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [anomalyCount, setAnomalyCount] = useState({});
  const [customLogo, setCustomLogo] = useState({ hasCustomLogo: false, logo_path: '', logo_thumb_path: '' });
  const [navbarVisible, setNavbarVisible] = useState(() => 
    localStorage.getItem('navbarVisible') !== 'false' // Default to visible
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => 
    localStorage.getItem('sidebarCollapsed') === 'true'
  );

  // Toggle functions for navbar and sidebar
  const toggleNavbar = () => {
    setNavbarVisible(prev => {
      const newState = !prev;
      localStorage.setItem('navbarVisible', newState);
      return newState;
    });
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => !prev);
  };

  // Set page title
  useEffect(() => {
    document.title = 'Parki | My Profile';
  }, []);

  // Fetch user profile data and reports for sidebar
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check if user is authenticated
        if (!auth.isAuthenticated()) {
          history.push('/login');
          return;
        }
        
        // Fetch user profile
        const profileData = await getUserProfile();

        // build user data variable
        const tmpUser = {
          firstName: profileData?.user?.given_name,
          lastName: profileData?.user?.family_name,
          userType: profileData?.userData?.userType || 'customer',
          entityName: profileData?.userData?.entityName || 'unknown',
          entityId: parseInt(profileData?.userData?.linkedEntity) || null,
          isEntityAdmin: profileData?.userData?.isEntityAdmin || false,
          userId: profileData?.userData?.userId || null,
          email: profileData?.user?.email || '',
          defaultTV: profileData?.userData?.DefaultTV || -1
        };

        // update user data in local storage
        auth.setCurrentUser(tmpUser);

        // Update user state with profile data
        setUser(tmpUser);

        // set the anomaly count
        setAnomalyCount(profileData?.anomaly_count || {});

        // set custom logo info
        setCustomLogo({
            hasCustomLogo: profileData?.custom_logo?.hasCustomLogo || false,
            logo_path: profileData?.custom_logo?.logo_path || '',
            logo_thumb_path: profileData?.custom_logo?.logo_thumb_path || ''
        });
        
        // Update form data
        setFormData({
          firstName: tmpUser.firstName || '',
          lastName: tmpUser.lastName || ''
        });

        // Store reports data for sidebar
        if (profileData?.reports && profileData.reports.length > 0) {
          setReports(profileData.reports);
        }

        setPageLoading(false);
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setPageLoading(false);
        
        // If unauthorized (401), redirect to login
        if (error.response && error.response.status === 401) {
          auth.logout();
          history.push('/login');
        }
      }
    };
    fetchData();
  }, [history]);

  // Handle input changes
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      // Call API to update user profile
      await updateUserProfile(formData);
      
      // Update local storage user data
      const currentUser = auth.getCurrentUser();
      const updatedUser = {
        ...currentUser,
        firstName: formData.firstName,
        lastName: formData.lastName
      };
      auth.setCurrentUser(updatedUser);
      setUser(updatedUser);
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000); // Hide success message after 3 seconds
    } catch (err) {
      setError('Failed to update profile. Please try again.');
      console.error('Error updating profile:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await auth.logout();
      history.push('/login');
    } catch (err) {
      console.error('Error logging out:', err);
      history.push('/login');
    }
  };
  
  // Handle report selection (redirect to dashboard)
  const handleReportSelect = (report) => {
    localStorage.setItem('selectedReport', JSON.stringify(report));
    history.push('/dashboard');
  };

  if (pageLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div id="dashboard" className={`layout-wrapper layout-content-navbar ${!navbarVisible ? 'layout-without-navbar' : ''}`}>
      <div className="layout-container">
        {/* Sidebar component */}
        <Sidebar 
          activeItem="profile" 
          collapsed={sidebarCollapsed} 
          toggleSidebar={toggleSidebar}
          reports={reports}
          selectedReport={selectedReport}
          onSelectReport={handleReportSelect}
          anomalyAlerts={anomalyCount}
          customLogo={customLogo}
          user={user}
        />

        {/* Layout page */}
        <div className="layout-page">
          {/* Navbar component */}
          {navbarVisible && (
            <Navbar 
              user={user} 
              toggleSidebar={toggleSidebar} 
              selectedReport={selectedReport}
              sidebarCollapsed={sidebarCollapsed}
            />
          )}

          {/* Content */}
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <h4 className="fw-bold py-3 mb-4">My Profile</h4>
              
              <div className="row">
                <div className="col-md-12">
                  <div className="card mb-4">
                    <h5 className="card-header">Profile Details</h5>
                    <div className="card-body pt-7">
                      {success && (
                        <div className="alert alert-success" role="alert">
                          Profile updated successfully!
                        </div>
                      )}
                      
                      {error && (
                        <div className="alert alert-danger" role="alert">
                          {error}
                        </div>
                      )}
                      
                      <form onSubmit={handleSubmit}>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label htmlFor="firstName" className="form-label">First Name</label>
                            <input
                              type="text"
                              className="form-control"
                              id="firstName"
                              name="firstName"
                              value={formData.firstName}
                              onChange={handleChange}
                              placeholder="First Name"
                            />
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="lastName" className="form-label">Last Name</label>
                            <input
                              type="text"
                              className="form-control"
                              id="lastName"
                              name="lastName"
                              value={formData.lastName}
                              onChange={handleChange}
                              placeholder="Last Name"
                            />
                          </div>
                        </div>
                        
                        <div className="mt-4">
                          <button 
                            type="submit" 
                            className="btn btn-primary me-2" 
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : 'Save Changes'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-12 mb-4">
                  <div className="card">
                    <h5 className="card-header">Account Actions</h5>
                    <div className="card-body pt-7">
                      <div className="mb-3 col-12">
                        <button 
                          className="btn btn-danger"
                          onClick={handleLogout}
                        >
                          <i className="icon-base ti tabler-logout me-1"></i>
                          Logout
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay */}
      <div className="layout-overlay layout-menu-toggle"></div>
      
      {/* Drag Target for Mobile */}
      <div className="drag-target"></div>

      <FloatingNavToggle visible={navbarVisible} onToggle={toggleNavbar} offset={0} />
    </div>
  );
};

export default MyProfile;